import { useCallback, useEffect, useState } from 'react';
import type React from 'react';


import { Stack } from 'ui/stack';

import Modal from 'components/partials/modal/modal';
import Button from 'components/partials/button/button';

import Icons from 'assets/icons';

import {
  dispatchAiAssistantExampleMessage,
  dispatchMarkStartHints,
} from '../../ai-assistant-events';
import {
  trackMarkWalkthroughCompleted,
  trackMarkWalkthroughDismissed,
  trackMarkWalkthroughPathSelected,
  trackMarkWalkthroughViewed,
} from '../../analytics/mark-walkthrough-analytics';
import type { MarkWalkthroughEntryPoint } from '../../analytics/mark-walkthrough-analytics.constants';

import MarkWalkthroughStep1 from './mark-walkthrough-step-1';
import MarkWalkthroughStep2 from './mark-walkthrough-step-2';
import MarkWalkthroughStep3 from './mark-walkthrough-step-3';
interface MarkWalkthroughModalProps {
  onClose: () => void;
  entryPoint: MarkWalkthroughEntryPoint;
}

const MarkWalkthroughModal = ({
  onClose,
  entryPoint,
}: MarkWalkthroughModalProps): React.JSX.Element => {
  const [stepNumber, setStepNumber] = useState(1);

  useEffect(() => {
    trackMarkWalkthroughViewed({
      section: 'walkthroughModal',
      stepNumber,
    });
  }, [stepNumber]);

  const handleDismiss = useCallback(
    (dismissMethod: 'maybeLater' | 'closeButton') => {
      trackMarkWalkthroughDismissed({
        section: 'walkthroughModal',
        dismissMethod,
        stepNumber,
      });
      onClose();
    },
    [onClose, stepNumber]
  );

  const handleBack = () => {
    setStepNumber((prev) => Math.max(prev - 1, 1));
  };

  const handleNext = () => {
    if (isLastStep) {
      trackMarkWalkthroughPathSelected({ path: 'startHintTour', entryPoint });
      dispatchMarkStartHints();
      onClose();

      return;
    }

    setStepNumber((prev) => Math.min(prev + 1, totalSteps));
  };

  const handleExamplePromptClick = (prompt: string, label: string) => {
    trackMarkWalkthroughPathSelected({
      path: 'sendExampleMessage',
      exampleLabel: label,
      entryPoint,
    });
    trackMarkWalkthroughCompleted({
      section: 'walkthroughModal',
      completionType: 'essentialExampleSent',
      exampleLabel: label,
      entryPoint,
    });
    dispatchAiAssistantExampleMessage(prompt);
    onClose();
  };

  const steps = [
    <MarkWalkthroughStep1 />,
    <MarkWalkthroughStep2 />,
    <MarkWalkthroughStep3 onExamplePromptClick={handleExamplePromptClick} />,
  ];

  const totalSteps = steps.length;
  const isFirstStep = stepNumber === 1;
  const isLastStep = stepNumber === totalSteps;

  const footer = (
    <Stack
      direction="row"
      sx={{ alignItems: 'center', justifyContent: 'space-between' }}
    >
      {isFirstStep ? (
        <Button
          type="button"
          variant="reserve-text-link"
          className="p-0"
          size="md"
          onClick={() => handleDismiss('maybeLater')}
          data-testid="mark-walkthrough-maybe-later"
        >
          Maybe later
        </Button>
      ) : (
        <Button
          type="button"
          variant="reserve-text-link"
          className="p-0"
          size="md"
          onClick={handleBack}
          data-testid="mark-walkthrough-back"
          iconBefore={<Icons.ChevronLeft />}
        >
          Back
        </Button>
      )}

      <Button
        type="button"
        variant="brand-filled"
        size="md"
        onClick={handleNext}
        data-testid="mark-walkthrough-next"
        iconAfter={!isLastStep ? <Icons.ChevronRight /> : undefined}
      >
        {isLastStep ? 'See it in action' : 'Next'}
      </Button>
    </Stack>
  );

  return (
    <Modal
      headerText=""
      stepIndicator={`Step ${stepNumber} of ${totalSteps}`}
      footer={footer}
      onCloseCallback={() => handleDismiss('closeButton')}
      showCloseButton
      disableBackdropClick
      disableEnterConfirm
      skipUnmountOnClose
      data-testid="mark-walkthrough-modal"
      modalClassName="p-5 !w-screen tablet:!w-[640px] tablet:!max-w-[640px] min-h-[640px]"
      headerClassName="font-bold"
    >
      {steps[stepNumber - 1]}
    </Modal>
  );
};

export default MarkWalkthroughModal;
